import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import MarketingNav from '../components/MarketingNav';
import MarketingFooter from '../components/MarketingFooter';
import SEO from '../components/SEO';
import { ArrowRight, Mail, Phone, MapPin, Send, MessageSquare, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

export default function KontaktPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', company: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error('Bitte füllen Sie alle Pflichtfelder aus.');
      return;
    }
    setLoading(true);
    try {
      const subject = encodeURIComponent(`LaizyLeads Anfrage von ${form.name}`);
      const body = encodeURIComponent(`Name: ${form.name}\nE-Mail: ${form.email}\nUnternehmen: ${form.company}\n\nNachricht:\n${form.message}`);
      window.location.href = `mailto:info@laizyones.de?subject=${subject}&body=${body}`;
      setSent(true);
      toast.success('Ihr E-Mail-Programm wurde geöffnet. Wir antworten innerhalb von 24 Stunden.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white text-gray-900" data-testid="kontakt-page">
      <SEO
        title="Kontakt – Kostenloses Beratungsgespräch zur B2B Leadgenerierung | LaizyLeads"
        description="Sprechen Sie mit unserem Team über hochwertige B2B Leadgenerierung. Kostenloses Beratungsgespräch, persönlicher Ansprechpartner, schnelle Antwort innerhalb von 24 Stunden."
        path="/kontakt"
        keywords="LaizyLeads Kontakt, Beratungsgespräch B2B Leadgenerierung, Demo Leadgenerator, neue Kunden gewinnen B2B"
      />
      <MarketingNav />

      {/* HERO */}
      <section className="relative pt-28 pb-12 sm:pt-32 sm:pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-50 via-white to-gray-50" />
        <div className="relative z-10 max-w-4xl mx-auto px-5 sm:px-8 text-center">
          <Badge className="mb-6 bg-emerald-100 text-emerald-700 border-emerald-200 px-3.5 py-1.5 text-sm font-medium">
            <MessageSquare className="w-3.5 h-3.5 mr-1.5" /> Kontakt
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] mb-6">
            Lassen Sie uns über Ihre<span className="block text-emerald-600">Leadgenerierung sprechen.</span>
          </h1>
          <p className="text-lg text-gray-600 leading-relaxed max-w-2xl mx-auto">
            Kostenloses Beratungsgespräch, ehrliche Einschätzung und – wenn es passt – ein kostenloser Test. Wir antworten innerhalb von 24 Stunden.
          </p>
        </div>
      </section>

      {/* CONTACT GRID */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">

            {/* LEFT: contact details */}
            <div className="lg:col-span-2 space-y-6">
              <h2 className="text-2xl font-bold tracking-tight">Direkt erreichbar</h2>
              <p className="text-gray-600 leading-relaxed">
                Sie haben es eilig? Rufen Sie uns direkt an oder schreiben Sie eine E-Mail. Sie erreichen LaizyLeads – ein Produkt der Laizyones aus Sachsen-Anhalt – Montag bis Freitag von 9:00 bis 18:00 Uhr.
              </p>
              <div className="space-y-4">
                <a href="mailto:info@laizyones.de" className="flex items-start gap-3 p-4 rounded-xl border border-gray-200 hover:border-emerald-300 hover:bg-emerald-50/30 transition-all" data-testid="contact-email">
                  <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center shrink-0">
                    <Mail className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">E-Mail</p>
                    <p className="text-base font-semibold text-gray-900">info@laizyones.de</p>
                  </div>
                </a>
                <a href="tel:+491797615911" className="flex items-start gap-3 p-4 rounded-xl border border-gray-200 hover:border-emerald-300 hover:bg-emerald-50/30 transition-all" data-testid="contact-phone">
                  <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center shrink-0">
                    <Phone className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Telefon</p>
                    <p className="text-base font-semibold text-gray-900">0179 7615911</p>
                  </div>
                </a>
                <div className="flex items-start gap-3 p-4 rounded-xl border border-gray-200" data-testid="contact-address">
                  <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center shrink-0">
                    <MapPin className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Adresse</p>
                    <p className="text-base font-semibold text-gray-900">Ferdinand-Lentjes-Str. 32<br />39167 Wellen, Deutschland</p>
                  </div>
                </div>
              </div>

              <div className="p-5 rounded-xl bg-emerald-50 border border-emerald-200 mt-8">
                <h3 className="font-semibold text-gray-900 mb-1">Beratung statt Verkaufsgespräch</h3>
                <p className="text-sm text-gray-700 leading-relaxed">
                  Wir prüfen ehrlich, ob LaizyLeads zu Ihrem Vertrieb passt. Wenn nicht, sagen wir es. Punkt.
                </p>
              </div>
            </div>

            {/* RIGHT: form */}
            <div className="lg:col-span-3">
              <div className="p-8 rounded-2xl border border-gray-200 bg-gray-50">
                <h2 className="text-2xl font-bold tracking-tight mb-2">Kostenloses Beratungsgespräch anfragen</h2>
                <p className="text-sm text-gray-600 mb-6">Schicken Sie uns kurz eine Nachricht – wir melden uns innerhalb von 24 Stunden.</p>

                {sent ? (
                  <div className="p-6 rounded-xl bg-white border border-emerald-200 text-center" data-testid="contact-success">
                    <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto mb-3" />
                    <p className="font-semibold text-gray-900 mb-1">Nachricht vorbereitet!</p>
                    <p className="text-sm text-gray-600">Falls sich Ihr E-Mail-Programm nicht öffnet, senden Sie bitte direkt an info@laizyones.de.</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm font-medium">Ihr Name *</Label>
                        <Input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} placeholder="Max Mustermann" data-testid="contact-name" required />
                      </div>
                      <div>
                        <Label className="text-sm font-medium">E-Mail *</Label>
                        <Input type="email" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} placeholder="max@firma.de" data-testid="contact-email-input" required />
                      </div>
                    </div>
                    <div>
                      <Label className="text-sm font-medium">Unternehmen</Label>
                      <Input value={form.company} onChange={e => setForm(f => ({...f, company: e.target.value}))} placeholder="Mustermann GmbH" data-testid="contact-company" />
                    </div>
                    <div>
                      <Label className="text-sm font-medium">Ihre Nachricht *</Label>
                      <Textarea rows={5} value={form.message} onChange={e => setForm(f => ({...f, message: e.target.value}))} placeholder="Worum geht's? Branche, Zielgruppe, aktuelle Vertriebssituation – alles hilfreich." data-testid="contact-message" required />
                    </div>
                    <Button type="submit" size="lg" className="w-full bg-emerald-600 hover:bg-emerald-500 text-white h-12" disabled={loading} data-testid="contact-submit">
                      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <span className="flex items-center justify-center gap-2"><Send className="w-4 h-4" /> Nachricht senden</span>}
                    </Button>
                    <p className="text-xs text-gray-500 text-center">Mit dem Absenden öffnen wir Ihr E-Mail-Programm zum Versand. Keine versteckte Datenspeicherung.</p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* INTERNAL LINKS */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <h2 className="text-2xl font-bold tracking-tight mb-6">Vorab informieren</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <Link to="/leistungen" className="group p-6 rounded-2xl border border-gray-200 bg-white hover:border-emerald-300 hover:shadow-md transition-all">
              <h3 className="font-semibold mb-1 group-hover:text-emerald-700">Leistungen</h3>
              <p className="text-sm text-gray-500">Was steckt im Leadgenerator drin?</p>
            </Link>
            <Link to="/branchen" className="group p-6 rounded-2xl border border-gray-200 bg-white hover:border-emerald-300 hover:shadow-md transition-all">
              <h3 className="font-semibold mb-1 group-hover:text-emerald-700">Branchen</h3>
              <p className="text-sm text-gray-500">Use Cases nach Branche.</p>
            </Link>
            <Link to="/case-studies" className="group p-6 rounded-2xl border border-gray-200 bg-white hover:border-emerald-300 hover:shadow-md transition-all">
              <h3 className="font-semibold mb-1 group-hover:text-emerald-700">Case Studies</h3>
              <p className="text-sm text-gray-500">Konkrete Ergebnisse aus B2B-Vertrieb.</p>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-600 to-emerald-700" />
        <div className="relative max-w-3xl mx-auto px-5 sm:px-8 text-center">
          <h2 className="text-3xl font-bold tracking-tight text-white mb-4">Lieber direkt loslegen?</h2>
          <p className="text-emerald-100 mb-6 max-w-xl mx-auto">Starten Sie sofort – Registrierung in 30 Sekunden, erste Leads in wenigen Minuten.</p>
          <Button size="lg" className="bg-white hover:bg-gray-100 text-emerald-700 h-12 px-8 text-base shadow-xl font-semibold" onClick={() => navigate('/register')}>
            Jetzt registrieren <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
